import {
  CSSProperties,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { InlineNotification, Loading, Tile } from '@carbon/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { debounce } from 'lodash-es';
import { DocumentResponse } from 'types/api-responses';
import { useDocumentChannelEvents } from 'lib/hooks/use-document-channel-events';
import { ProvisionsHeaderSlotsContext } from 'components/provisioned-document';
import { HorizontalDraggableDivider } from 'components/draggable-divider';
import { FormattedCount } from 'components/formatted-count';
import { format0 } from 'utils/number';
import { StatusTag, type PartyStatus } from 'components/status-tag';
import {
  documentLayoutAtom,
  leftPanelWidthAtom,
  LEFT_PANEL_MAX_WIDTH_PX,
  LEFT_PANEL_MIN_WIDTH_PX,
} from './atoms/document-layout';
import {
  fetchStructuredPartiesListAtom,
  selectedPartyIndexAtom,
  structuredPartiesListDataAtom,
} from './atoms/structured-parties';
import { auditLogVersionAtom } from './atoms/structured-addresses';
import { PartiesOverview } from './parties-overview';
import { DocumentSuggestionsBanner } from './document-matching-banner';
import { PAGE_CONTENT_SELECTOR } from './exclusive-drawer-hooks';
import { StructuringTree } from './structuring-tree';
import { buildOverviewTree, OVERVIEW_ANCHORS, pagedDrillIn } from './utils';
import { DocumentAddressesSection } from './document-addresses-section';
import { DocumentAuditLogSection } from './document-audit-log-section';
import { VendorInformationSection } from './vendor-information-section';
import {
  useAfeStructuring,
  useDivisionOrderStructuring,
  useDocumentRerun,
  useOverviewScrollSpy,
  usePinnedHeaderOffset,
  useRoyaltyCheckStructuring,
  type PartyChannelPayload,
  isOwnPush,
  PARTY_PUSH_EVENTS,
} from './hooks';
import {
  AfeCostDrillIn,
  LeftPanelToggle,
  PartyDrillIn,
  WellDrillInView,
} from './drill-in-views';
// The doctype sections render as further children of the Structured Overview,
// each keyed purely off the document id. Their export names collide across
// doctypes, so DO and RC imports carry a `Do` / `Rc` prefix.
import { IdentificationSection } from 'pages/afe-catalog/structuring/identification-section';
import { WellsOverviewSection } from 'pages/afe-catalog/structuring/wells-overview-section';
import { WellReviewMatch } from 'pages/afe-catalog/structuring/well-review-match';
import { CostOverviewSection } from 'pages/afe-catalog/structuring/cost-overview-section';
import { RelatedItemsSection } from 'components/related-items';
import { OverviewSection } from 'pages/division-orders/structuring/overview-section';
import { InterestOverviewSection } from 'pages/division-orders/structuring/interest-overview-section';
import { WellsOverviewSection as DoWellsOverviewSection } from 'pages/division-orders/structuring/wells-overview-section';
import { WellReviewMatch as DoWellReviewMatch } from 'pages/division-orders/structuring/well-review-match';
import { OverviewSection as RcOverviewSection } from 'pages/royalty-checks/structuring/overview-section';
import { LineItemReviewSection as RcLineItemReviewSection } from 'pages/royalty-checks/structuring/line-item-review-section';
import { LineItemReviewDetail as RcLineItemReviewDetail } from 'pages/royalty-checks/structuring/line-item-review-detail';
import { FinancialSummarySection as RcFinancialSummarySection } from 'pages/royalty-checks/structuring/financial-summary-section';
import { WellsOverviewSection as RcWellsOverviewSection } from 'pages/royalty-checks/structuring/wells-overview-section';
import { WellReviewMatch as RcWellReviewMatch } from 'pages/royalty-checks/structuring/well-review-match';
import type {
  OverviewAnchor,
  OverviewSectionDescriptor,
  WellDrillIn,
} from './types';
import style from './structuring-module.module.scss';

type Section = 'provisions' | 'structured-overview';

const PUSH_REFRESH_DEBOUNCE_MS = 250;
const PUSH_REFRESH_MAX_WAIT_MS = 1000;

interface Props {
  document: DocumentResponse;
  provisionsSlot: ReactNode;
  legalDescriptionSlot?: ReactNode;
  legalDescriptionTitle?: string;
}

/**
 * The document's structuring shell: the section tree beside Provisions or the Structured
 * Overview, with its drill-ins.
 *
 * @param {Props} props - The document, and the Provisions and legal description slots.
 * @return {JSX.Element} The shell.
 */
export const StructuringModule: FC<Props> = ({
  document,
  provisionsSlot,
  legalDescriptionSlot,
  legalDescriptionTitle = 'Full Legal Description',
}) => {
  const [section, setSection] = useState<Section>('provisions');
  const layout = useAtomValue(documentLayoutAtom);
  const [leftPanelWidth, setLeftPanelWidth] = useAtom(leftPanelWidthAtom);
  const [titleSlotEl, setTitleSlotEl] = useState<HTMLElement | null>(null);
  const [actionsSlotEl, setActionsSlotEl] = useState<HTMLElement | null>(null);
  const headerSlots = useMemo(
    () => ({ title: titleSlotEl, actions: actionsSlotEl }),
    [titleSlotEl, actionsSlotEl]
  );
  const partiesListData = useAtomValue(structuredPartiesListDataAtom);
  const reloadPartiesList = useSetAtom(fetchStructuredPartiesListAtom);
  const setAuditLogVersion = useSetAtom(auditLogVersionAtom);
  const [selectedPartyIndex, setSelectedPartyIndex] = useAtom(
    selectedPartyIndexAtom
  );

  // Every document reaches this shell through DocumentOverviewPage, and the
  // same instance is reused across a document switch, so the doctype hooks all
  // run unconditionally; `enabled` gates their fetches.
  const isAfe = document.type === 'Document::Afe';
  const isDivisionOrder = document.type === 'Document::DivisionOrder';
  const isRoyaltyCheck = document.type === 'Document::RoyaltyCheck';

  const showParties = !!document.resources.structuredParties;
  const parties = partiesListData?.parties ?? [];
  const partiesListHref = document.resources.structuredParties?.href;
  const agentHasRun = partiesListData?.agentHasRun ?? true;
  const showUnstructuredBanner =
    showParties && !agentHasRun && parties.length > 0;
  const { isRunning, rerunError, clearRerunError, rerun } = useDocumentRerun({
    rerunHref: partiesListData?.resources?.suggestionRerun?.href,
    partiesListHref,
    agentRunInProgress: partiesListData?.agentRunInProgress ?? false,
  });

  const afe = useAfeStructuring({
    documentId: document.id,
    enabled: isAfe,
    rerunRunning: isRunning,
    onDeepLink: () => setSection('structured-overview'),
  });
  const divisionOrder = useDivisionOrderStructuring({
    documentId: document.id,
    enabled: isDivisionOrder,
  });
  const royaltyCheck = useRoyaltyCheckStructuring({
    documentId: document.id,
    enabled: isRoyaltyCheck,
  });

  // Hydrate the parties list as soon as the document mounts so the tree's
  // status icon and per-party leaves render on first paint, not only once the
  // user opens Structured Overview (where PartiesOverview loads it).
  useEffect(() => {
    if (partiesListHref) reloadPartiesList(partiesListHref);
  }, [partiesListHref, reloadPartiesList]);

  usePinnedHeaderOffset();
  const activeAnchor = useOverviewScrollSpy(
    (isAfe || isDivisionOrder || isRoyaltyCheck) &&
      section === 'structured-overview',
    [
      showParties,
      afe.selectedWellIndex,
      afe.selectedCostWellId,
      divisionOrder.selectedWellIndex,
      royaltyCheck.selectedWellIndex,
      royaltyCheck.selectedLineItemIndex,
    ].join('|')
  );

  const documentAddresses = partiesListData?.documentAddresses ?? [];
  const selectedParty =
    selectedPartyIndex !== null ? (parties[selectedPartyIndex] ?? null) : null;

  const partiesSummary = partiesListData?.summary;
  const addressesSummary = partiesListData?.documentAddressesSummary;
  const activeParties = parties.filter((p) => p.status !== 'removed');

  const addressesSectionStatus: PartyStatus =
    addressesSummary?.status ?? 'not_started';
  const addressesActiveCount = documentAddresses.filter(
    (a) => a.status !== 'removed' && !a.removed
  ).length;
  const addressesCompleteCount = addressesSummary?.counts.complete ?? 0;

  // The Well Match Review drill-in for the active doctype; one path
  // (WellDrillInView) serves AFE, DO and RC wells.
  const wellDrillIn: WellDrillIn | null =
    isAfe && afe.selectedWell && afe.selectedWellIndex !== null
      ? pagedDrillIn({
          nodeId: `afe-well-${afe.selectedWell.id}`,
          index: afe.selectedWellIndex,
          count: afe.wells.length,
          setIndex: afe.setSelectedWellIndex,
          renderMatch: () => (
            <WellReviewMatch
              documentId={document.id}
              well={afe.selectedWell!}
              index={afe.selectedWellIndex ?? 0}
              total={afe.wells.length}
              onMutated={() => {
                afe.reloadWells();
                // A well rename flows to its catalog header, so the Cost
                // "Well" column and side nav refresh too.
                afe.reloadCosts();
              }}
            />
          ),
        })
      : isDivisionOrder &&
          divisionOrder.selectedWell &&
          divisionOrder.selectedWellIndex !== null
        ? pagedDrillIn({
            nodeId: `do-well-${divisionOrder.selectedWell.id}`,
            index: divisionOrder.selectedWellIndex,
            count: divisionOrder.wells.length,
            setIndex: divisionOrder.setSelectedWellIndex,
            renderMatch: () => (
              <DoWellReviewMatch
                documentId={document.id}
                well={divisionOrder.selectedWell!}
                index={divisionOrder.selectedWellIndex ?? 0}
                total={divisionOrder.wells.length}
                onMutated={() => {
                  divisionOrder.reloadWells();
                  // Confirm / un-match writes the well's name onto the
                  // property's interest lines, so Interest Review refreshes too.
                  divisionOrder.reloadInterests();
                }}
              />
            ),
          })
        : isRoyaltyCheck &&
            royaltyCheck.selectedWell &&
            royaltyCheck.selectedWellIndex !== null
          ? pagedDrillIn({
              nodeId: `rc-well-${royaltyCheck.selectedWell.id}`,
              index: royaltyCheck.selectedWellIndex,
              count: royaltyCheck.wells.length,
              setIndex: royaltyCheck.setSelectedWellIndex,
              renderMatch: () => (
                <RcWellReviewMatch
                  documentId={document.id}
                  well={royaltyCheck.selectedWell!}
                  index={royaltyCheck.selectedWellIndex ?? 0}
                  total={royaltyCheck.wells.length}
                  onMutated={royaltyCheck.reloadWells}
                />
              ),
            })
          : null;

  // RC Line Item Review drill-in, paged over the well GROUPS (independent of
  // the well drill-in above).
  const lineItemDrillIn: WellDrillIn | null =
    isRoyaltyCheck &&
    royaltyCheck.selectedLineItemGroup &&
    royaltyCheck.selectedLineItemIndex !== null
      ? pagedDrillIn({
          nodeId: `rc-line-item-${royaltyCheck.selectedLineItemGroup.key}`,
          index: royaltyCheck.selectedLineItemIndex,
          count: royaltyCheck.lineItemGroups.length,
          setIndex: royaltyCheck.setSelectedLineItemIndex,
          renderMatch: () => (
            <RcLineItemReviewDetail
              documentId={document.id}
              index={royaltyCheck.selectedLineItemIndex ?? 0}
              group={royaltyCheck.selectedLineItemGroup!}
              onMutated={royaltyCheck.reloadLineItems}
            />
          ),
        })
      : null;

  const activeNodeId = (() => {
    if (section === 'provisions') return 'provisions';
    if (selectedParty) return `party-${selectedParty.kind}-${selectedParty.id}`;
    // Well drill-ins take precedence over the AFE cost drill-in.
    if (wellDrillIn) return wellDrillIn.nodeId;
    if (lineItemDrillIn) return lineItemDrillIn.nodeId;
    if (isAfe && afe.selectedCostWellId !== null)
      return `afe-cost-well-${afe.selectedCostWellId}`;
    // Scroll-spy: highlight the child node for the section in view.
    if (activeAnchor) {
      return (
        OVERVIEW_ANCHORS.find((a) => a.anchor === activeAnchor)?.node ??
        'structured-overview'
      );
    }
    return 'structured-overview';
  })();

  const { setSelectedWellIndex: setAfeWellIndex, setSelectedCostWellId } = afe;
  const setDoWellIndex = divisionOrder.setSelectedWellIndex;
  const {
    setSelectedWellIndex: setRcWellIndex,
    setSelectedLineItemIndex: setRcLineItemIndex,
  } = royaltyCheck;
  const goToOverviewSection = useCallback(
    (anchorId: OverviewAnchor) => {
      setSection('structured-overview');
      setSelectedPartyIndex(null);
      setAfeWellIndex(null);
      setSelectedCostWellId(null);
      setDoWellIndex(null);
      setRcWellIndex(null);
      setRcLineItemIndex(null);
      // Defer to the next paint so the section's anchor target is mounted.
      window.requestAnimationFrame(() => {
        const el = window.document.getElementById(anchorId);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    [
      setSelectedPartyIndex,
      setAfeWellIndex,
      setSelectedCostWellId,
      setDoWellIndex,
      setRcWellIndex,
      setRcLineItemIndex,
    ]
  );

  /** Shows the Structured Overview with no party open. */
  const showStructuredOverviewAt = () => {
    setSection('structured-overview');
    setSelectedPartyIndex(null);
  };

  const overviewNodes = showParties
    ? buildOverviewTree({
        parties,
        partiesStatus: partiesSummary?.status ?? 'not_started',
        addressesStatus: addressesSectionStatus,
        afe: isAfe
          ? {
              identificationStatus: afe.identificationStatus,
              wellsSection: {
                status: afe.wellOverview?.summary?.status ?? 'not_started',
                running: !!afe.wellOverview?.agentRunInProgress,
                wells: afe.wells,
                onSelectWell: (index) => {
                  showStructuredOverviewAt();
                  setSelectedCostWellId(null);
                  setAfeWellIndex(index);
                },
              },
              costStatus: afe.costSectionStatus,
              costWells: afe.costWells,
              onSelectCostWell: (afeHeaderId) => {
                showStructuredOverviewAt();
                setAfeWellIndex(null);
                setSelectedCostWellId(afeHeaderId);
              },
            }
          : null,
        divisionOrder: isDivisionOrder
          ? {
              overviewStatus: divisionOrder.overviewStatus,
              wellsSection: {
                status:
                  divisionOrder.wellOverview?.summary?.status ?? 'not_started',
                running: !!divisionOrder.wellOverview?.agentRunInProgress,
                wells: divisionOrder.wells,
                onSelectWell: (index) => {
                  showStructuredOverviewAt();
                  setDoWellIndex(index);
                },
              },
              interestStatus: divisionOrder.interestSectionStatus,
            }
          : null,
        royaltyCheck: isRoyaltyCheck
          ? {
              overviewStatus: royaltyCheck.overviewStatus,
              wellsSection: {
                status:
                  royaltyCheck.wellOverview?.summary?.status ?? 'not_started',
                running: !!royaltyCheck.wellOverview?.agentRunInProgress,
                wells: royaltyCheck.wells,
                onSelectWell: (index) => {
                  showStructuredOverviewAt();
                  setRcLineItemIndex(null);
                  setRcWellIndex(index);
                },
              },
              lineItemStatus: royaltyCheck.lineItemSectionStatus,
              lineItemGroups: royaltyCheck.lineItemGroups,
              onSelectLineItemGroup: (index) => {
                showStructuredOverviewAt();
                setRcWellIndex(null);
                setRcLineItemIndex(index);
              },
              financialSummaryStatus: royaltyCheck.summaryAvailable
                ? royaltyCheck.summaryStatus
                : null,
            }
          : null,
        goToSection: goToOverviewSection,
        onSelectParty: (index) => {
          setSection('structured-overview');
          setSelectedPartyIndex(index);
        },
      })
    : null;

  /** Reloads the parties list and the document's audit log. */
  const refresh = useCallback(() => {
    if (partiesListHref) reloadPartiesList(partiesListHref);
    setAuditLogVersion((v) => v + 1);
  }, [partiesListHref, reloadPartiesList, setAuditLogVersion]);

  // One action pushes several events (a reconcile pushes to each document it
  // touches), so a burst collapses into one refresh; a steady stream still
  // refreshes once a second.
  const debouncedRefresh = useMemo(
    () =>
      debounce(refresh, PUSH_REFRESH_DEBOUNCE_MS, {
        maxWait: PUSH_REFRESH_MAX_WAIT_MS,
      }),
    [refresh]
  );
  useEffect(() => () => debouncedRefresh.cancel(), [debouncedRefresh]);

  /**
   * Refreshes on every push other than this tab's own; StructuringModule owns
   * the list refresh. A change through a shared anchor can land from another
   * document, so whichever view is open refreshes.
   *
   * @param {PartyChannelPayload} data - The push.
   */
  const refreshOnPush = (data?: PartyChannelPayload) => {
    if (!isOwnPush(data)) debouncedRefresh();
  };
  useDocumentChannelEvents(
    document.notifications?.channel,
    Object.fromEntries(PARTY_PUSH_EVENTS.map((name) => [name, refreshOnPush]))
  );

  /**
   * The Provisions section.
   *
   * @return {JSX.Element} The section.
   */
  const renderProvisions = () => (
    <div className={style.sectionWrapper}>
      <div className={style.sectionHeader}>
        <div ref={setTitleSlotEl} className={style.sectionTitleSlot} />
        <div className={style.sectionNav}>
          <LeftPanelToggle />
          <div ref={setActionsSlotEl} className={style.sectionNav} />
        </div>
      </div>
      <ProvisionsHeaderSlotsContext.Provider value={headerSlots}>
        <div className={style.provisionsColumns}>
          <div className={style.provisionsColLeft}>{provisionsSlot}</div>
          <div className={style.provisionsColRight}>
            {legalDescriptionTitle && (
              <p className={style.provisionsColRightTitle}>
                {legalDescriptionTitle}
              </p>
            )}
            {legalDescriptionSlot}
          </div>
        </div>
      </ProvisionsHeaderSlotsContext.Provider>
    </div>
  );

  /**
   * The Structured Overview, or the drill-in open in it.
   *
   * @return {JSX.Element} The overview or drill-in.
   */
  const renderStructuredOverview = () => {
    if (selectedParty && selectedPartyIndex !== null)
      return (
        <PartyDrillIn
          document={document}
          parties={parties}
          index={selectedPartyIndex}
          onSelectIndex={setSelectedPartyIndex}
          onMutated={refresh}
        />
      );
    // One drill-in path for AFE + DO wells; the AFE cost drill-in keeps its
    // former precedence (well before cost). See wellDrillIn above.
    if (wellDrillIn) return <WellDrillInView drillIn={wellDrillIn} />;
    if (lineItemDrillIn)
      return <WellDrillInView drillIn={lineItemDrillIn} title="Line Items" />;
    if (isAfe && afe.selectedCostWellId !== null)
      return (
        <AfeCostDrillIn
          documentId={document.id}
          costWells={afe.costWells}
          selectedCostWellId={afe.selectedCostWellId}
          onSelect={setSelectedCostWellId}
          onMutated={afe.reloadCosts}
        />
      );

    // Ordered doctype section list for the combined Structured Overview body.
    // The shell wraps each in a scroll-spy <section>; content + loading gates are
    // owned by the descriptor, so isAfe / isDivisionOrder collapse to "pick the
    // list". Parties / Non-Party Addresses / Activity Log are shared across all
    // documents and stay inline below.

    // Document-scoped Related Items — identical across doctypes; anchored to the
    // structuring shell so the slide-in Link panel pushes the page, not overlays.
    const relatedItemsSection = (
      <RelatedItemsSection
        host={{
          basePath: `/documents/${document.id}`,
          notifications: document.notifications,
        }}
        selectorPageContent={PAGE_CONTENT_SELECTOR}
      />
    );

    const overviewSections: OverviewSectionDescriptor[] = isAfe
      ? [
          {
            anchorId: 'so-afe-identification',
            content: <IdentificationSection documentId={document.id} />,
          },
          {
            anchorId: 'so-afe-wells',
            content:
              afe.wellOverviewLoading && !afe.wellOverview ? (
                <Loading
                  withOverlay={false}
                  small
                  description="Loading wells…"
                />
              ) : (
                <WellsOverviewSection documentId={document.id} />
              ),
          },
          {
            anchorId: 'so-afe-costs',
            content: (
              <CostOverviewSection
                documentId={document.id}
                onSelectWell={setSelectedCostWellId}
              />
            ),
          },
          {
            anchorId: 'so-afe-related-items',
            content: relatedItemsSection,
          },
        ]
      : isDivisionOrder
        ? [
            {
              anchorId: 'so-do-overview',
              content: <OverviewSection documentId={document.id} />,
            },
            {
              anchorId: 'so-do-wells',
              content:
                divisionOrder.wellOverviewLoading &&
                !divisionOrder.wellOverview ? (
                  <Loading
                    withOverlay={false}
                    small
                    description="Loading wells…"
                  />
                ) : (
                  <DoWellsOverviewSection documentId={document.id} />
                ),
            },
            {
              anchorId: 'so-do-interests',
              content:
                divisionOrder.interestOverviewLoading &&
                !divisionOrder.interestOverview ? (
                  <Loading
                    withOverlay={false}
                    small
                    description="Loading interests…"
                  />
                ) : (
                  <InterestOverviewSection documentId={document.id} />
                ),
            },
            {
              anchorId: 'so-do-related-items',
              content: relatedItemsSection,
            },
          ]
        : isRoyaltyCheck
          ? [
              {
                anchorId: 'so-rc-overview',
                content: <RcOverviewSection documentId={document.id} />,
              },
              {
                anchorId: 'so-rc-wells',
                content:
                  royaltyCheck.wellOverviewLoading &&
                  !royaltyCheck.wellOverview ? (
                    <Loading
                      withOverlay={false}
                      small
                      description="Loading wells…"
                    />
                  ) : (
                    <RcWellsOverviewSection documentId={document.id} />
                  ),
              },
              {
                anchorId: 'so-rc-line-items',
                content: (
                  <RcLineItemReviewSection
                    documentId={document.id}
                    onSelectWell={(index) => {
                      setSelectedPartyIndex(null);
                      setRcWellIndex(null);
                      setRcLineItemIndex(index);
                    }}
                  />
                ),
              },
              // Financial Summary is PDF-only — omit the section (+ its tree node)
              // entirely for CSV/CDEX checks.
              ...(royaltyCheck.summaryAvailable
                ? [
                    {
                      anchorId: 'so-rc-financial-summary',
                      content: (
                        <RcFinancialSummarySection documentId={document.id} />
                      ),
                    },
                  ]
                : []),
              {
                anchorId: 'so-rc-related-items',
                content: relatedItemsSection,
              },
            ]
          : [
              // Generic documents (no doctype-specific sections) get the read-only
              // county-index Vendor Information panel plus the document-scoped
              // Related Items section.
              {
                anchorId: 'so-doc-vendor-information',
                content: <VendorInformationSection document={document} />,
              },
              {
                anchorId: 'so-doc-related-items',
                content: relatedItemsSection,
              },
            ];

    return (
      <div className={style.sectionWrapper}>
        <div className={style.sectionHeader}>
          <div className={style.sectionTitleSlot}>
            <h2>Structured Overview</h2>
          </div>
          <div className={style.sectionNav}>
            <LeftPanelToggle />
          </div>
        </div>
        <div className={style.overviewBody}>
          <section
            id="so-parties"
            className={style.overviewSection}
            aria-labelledby="so-parties-heading"
          >
            <div className={style.overviewSectionTitleRow}>
              <h4
                id="so-parties-heading"
                className={style.overviewSectionTitle}
              >
                Parties
              </h4>
              {partiesSummary && (
                <StatusTag
                  status={partiesSummary.status}
                  progress={`${format0(partiesSummary.counts.complete)}/${format0(activeParties.length)}`}
                />
              )}
            </div>
            <PartiesOverview document={document} />
          </section>

          <section
            id="so-doc-addresses"
            className={style.overviewSection}
            aria-labelledby="so-doc-addresses-heading"
          >
            <div className={style.overviewSectionTitleRow}>
              <h4
                id="so-doc-addresses-heading"
                className={style.overviewSectionTitle}
              >
                Non-Party Addresses
              </h4>
              {addressesActiveCount > 0 && (
                <StatusTag
                  status={addressesSectionStatus}
                  progress={`${format0(addressesCompleteCount)}/${format0(addressesActiveCount)}`}
                />
              )}
            </div>
            {addressesSummary && (
              <div className={style.overviewSummaryTiles}>
                <Tile>
                  <p className={style.overviewTileCount}>
                    <FormattedCount
                      value={addressesSummary.counts.notStarted}
                    />
                  </p>
                  <p className={style.overviewTileLabel}>Not Started</p>
                </Tile>
                <Tile>
                  <p className={style.overviewTileCount}>
                    <FormattedCount value={addressesSummary.counts.complete} />
                  </p>
                  <p className={style.overviewTileLabel}>Correct</p>
                </Tile>
                <Tile>
                  <p className={style.overviewTileCount}>
                    <FormattedCount value={addressesSummary.counts.removed} />
                  </p>
                  <p className={style.overviewTileLabel}>Removed</p>
                </Tile>
                <Tile>
                  <p className={style.overviewTileCount}>
                    <FormattedCount
                      value={addressesSummary.counts.userEdited}
                    />
                  </p>
                  <p className={style.overviewTileLabel}>User Edited</p>
                </Tile>
                <Tile>
                  <p className={style.overviewTileCount}>
                    <FormattedCount value={addressesSummary.counts.userAdded} />
                  </p>
                  <p className={style.overviewTileLabel}>User Added</p>
                </Tile>
              </div>
            )}
            <DocumentAddressesSection
              documentId={document.id}
              addresses={documentAddresses}
              parties={parties}
              onChange={refresh}
              embedded
            />
          </section>

          {overviewSections.map((s) => (
            <section
              key={s.anchorId}
              id={s.anchorId}
              className={style.overviewSection}
            >
              {s.content}
            </section>
          ))}

          <section
            id="so-audit-log"
            className={style.overviewSection}
            aria-label="Activity Log"
          >
            {/* No section <h4> here. DocumentAuditLogSection renders PartyAuditLog,
                which already shows its own "Activity Log · N events" heading — the
                same single-heading treatment the Parties drill-in uses. Adding an
                outer title here doubled up the "Activity Log" heading. */}
            <DocumentAuditLogSection documentId={document.id} />
          </section>
        </div>
      </div>
    );
  };

  /**
   * The section the examiner has open.
   *
   * @return {JSX.Element} The section.
   */
  const renderSection = () => {
    if (section === 'structured-overview') return renderStructuredOverview();
    return renderProvisions();
  };

  return (
    <div
      className={`${style.wrapper} structuring-shell${
        isAfe ? ' afe-structuring-shell' : ''
      }${isDivisionOrder ? ' do-structuring-shell' : ''}${
        isRoyaltyCheck ? ' rc-structuring-shell' : ''
      }`}
    >
      {showUnstructuredBanner && (
        <DocumentSuggestionsBanner running={isRunning} onRun={rerun} />
      )}
      {rerunError && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Error"
          subtitle={rerunError}
          onCloseButtonClick={clearRerunError}
        />
      )}
      <div className={style.body}>
        {layout.showLeftPanel && (
          <nav
            className={style.leftNav}
            aria-label="Structuring sections"
            style={
              {
                '--left-panel-width': `${leftPanelWidth}px`,
                '--left-panel-min-width': `${LEFT_PANEL_MIN_WIDTH_PX}px`,
              } as CSSProperties
            }
          >
            <StructuringTree
              activeNodeId={activeNodeId}
              overviewNodes={overviewNodes}
              onSelectProvisions={() => {
                setSection('provisions');
                setSelectedPartyIndex(null);
              }}
              onSelectOverview={showStructuredOverviewAt}
            />
          </nav>
        )}
        {layout.showLeftPanel && (
          <HorizontalDraggableDivider
            className={style.leftNavDivider}
            side="left"
            minWidth={LEFT_PANEL_MIN_WIDTH_PX}
            maxWidth={LEFT_PANEL_MAX_WIDTH_PX}
            onDrag={setLeftPanelWidth}
            value={leftPanelWidth}
            label="Resize the left panel"
          />
        )}
        <main className={style.main}>{renderSection()}</main>
      </div>
    </div>
  );
};
